import React, { useState, useEffect } from 'react';

export const secureUrl = (url) =>
  typeof url === 'string' && url.startsWith('http://') && window.location.protocol === 'https:'
    ? `https://${url.slice(7)}`
    : url;

const RESIZABLE_HOST = 'image.aapkabazar.co';

export const optimizedUrl = (url, width) => {
  const safe = secureUrl(url);
  if (typeof safe !== 'string' || !safe.includes(RESIZABLE_HOST)) return safe;

  try {
    const parsed = new URL(safe);
    parsed.searchParams.set('type', 'webp');
    if (width) parsed.searchParams.set('width', String(width));
    return parsed.toString();
  } catch {
    // A malformed URL is better rendered as-is than dropped.
    return safe;
  }
};

const Image = ({
  src,
  alt,
  className,
  width,
  height,
  priority = false,
  style,
  objectFit = 'cover',
  webpSrc,
  avifSrc,
  displayWidth,
  ...props
}) => {
  const [error, setError] = useState(false);
  useEffect(() => setError(false), [src]);
  const loadingAttr = priority ? "eager" : "lazy";

  const fallbackImage = "/placeholder.png";
  // `displayWidth` is the CSS width this renders at; the host is asked for that
  // size rather than the full-resolution original.
  const targetWidth = displayWidth || (typeof width === "number" ? width : undefined);
  const finalSrc = error
    ? fallbackImage
    : (optimizedUrl(src, targetWidth) || fallbackImage);

  // Retina phones paint 2x, so offer a double-size file for them to pick.
  const srcSet =
    !error && targetWidth && finalSrc !== fallbackImage
      ? `${finalSrc} 1x, ${optimizedUrl(src, targetWidth * 2)} 2x`
      : undefined;

  return (
    <picture className={className ? `picture-${className}` : ''}>
      {avifSrc && <source srcSet={avifSrc} type="image/avif" />}
      {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
      <img
        src={finalSrc}
        srcSet={srcSet}
        alt={alt || "Image"}
        className={className}
        width={width}
        height={height}
        loading={loadingAttr}
        fetchpriority={priority ? "high" : "auto"}
        style={{ ...style, objectFit }}
        onError={() => setError(true)}
        {...props}
      />
    </picture>
  );
};

export default Image;
